import type { Dispatch, SetStateAction } from 'react'
import type { IClientUser } from '../../interfaces/user';
import React, {
  Fragment,
  useContext,
  useState
} from 'react';

import { Link } from 'react-router-dom';
import { CommentCategory } from '../../components/Comment/constants';
import Navigation from '../../components/Navigation';
import { ProjectFeed } from '../../components/Project/ProjectFeed';
import ProjectTabs, { ProjectTabType } from '../../components/Project/ProjectTabs';
import UserCard from '../../components/User/UserCard';
import UserProfileModal from '../../components/User/UserProfileModal';
import {
  CurrentUserContext,
  CurrentUserContextProvider
} from '../../contexts/currentUserContext';
import {
  UserProjectsContext,
  UserProjectsContextProvider
} from '../../contexts/userProjectsContext';
import ProfileMainTabs, { ProfileMainTabType } from '../../components/User/ProfileMainTabs';
import PortfolioGrid from '../../components/Portfolio/PortfolioGrid';
import { PortfolioContextProvider } from '../../contexts/portfolioContext';

const CurrentUserProjects = () => {
  const projects = useContext(UserProjectsContext);
  const user = useContext(CurrentUserContext);
  const [activeTab, setActiveTab] = useState<ProjectTabType>(ProjectTabType.ACTIVE);

  const filteredProjects = projects
    ? projects.filter((project) =>
        activeTab === ProjectTabType.ACTIVE
          ? !project.isArchived
          : project.isArchived
      )
    : [];

  const activeCount = projects ? projects.filter((p) => !p.isArchived).length : 0;
  const archivedCount = projects ? projects.filter((p) => p.isArchived).length : 0;

  const hasAnyProjects = projects && projects.length > 0;
  const hasFilteredProjects = filteredProjects.length > 0;

  return (
    <article className="tile is-child">
      {hasAnyProjects && (
        <ProjectTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          activeCount={activeCount}
          archivedCount={archivedCount}
        />
      )}

      {hasFilteredProjects ? (
        filteredProjects.map((project, idx) => (
          <ProjectFeed
            key={idx}
            project={project}
            user={user}
            commentFrom={CommentCategory.userProjectListing}
          />
        ))
      ) : (
        <article className="tile is-child page-user-profile-next">
          <div className="card page-user-profile-next-card is-rounded is-dropshadowed">
            <header className="card-header page-user-profile-next-card-header header-padding has-text-centered is-block">
              <h1 className="has-text-weight-bold is-size-5">
                {activeTab === ProjectTabType.ACTIVE
                  ? hasAnyProjects
                    ? 'No active projects'
                    : 'Post your project, find collaborators!'
                  : 'No archived projects'}
              </h1>
              <p className="is-small m-b-1">
                {activeTab === ProjectTabType.ACTIVE
                  ? hasAnyProjects
                    ? 'All your projects are archived.'
                    : 'Share your project with the community.'
                  : 'You haven\'t archived any projects yet.'}
              </p>
              {activeTab === ProjectTabType.ACTIVE && !hasAnyProjects && (
                <Link to="/project/new">
                  <button className="button is-size-regular is-primary">
                    <span className="has-text-weight-bold is-small">
                      New Project
                    </span>
                  </button>
                </Link>
              )}
            </header>
            <div className="card-content is-paddingless is-rounded">
            </div>
          </div>
        </article>
      )}
    </article>
  );
};

const CurrentUserCard = ({
  setIsOpen
}: {
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const currentUser = useContext(CurrentUserContext);
  if (currentUser.id) {
    return (
      <UserCard currentUser={currentUser} setIsUserProfileModal={setIsOpen} />
    );
  }
  return null;
};

const ProjectsWrapper = () => {
  const user = useContext(CurrentUserContext);

  return (
    <UserProjectsContextProvider userId={user.id}>
      <CurrentUserProjects />
    </UserProjectsContextProvider>
  );
};

const PortfolioWrapper = () => {
  const user = useContext(CurrentUserContext);

  return (
    <PortfolioContextProvider userId={user.id}>
      <PortfolioGrid isOwner={true} />
    </PortfolioContextProvider>
  );
};

const CurrentUserProfile: React.FC<IClientUser> = (props: IClientUser) => {
  const [modalIsOpen, setIsOpen] = useState(false);
  const [mainTab, setMainTab] = useState<ProfileMainTabType>(ProfileMainTabType.PROJECT);
  const handleModalToggle = () => setIsOpen(!modalIsOpen);

  return (
    <Fragment>
      <main className="page-user-profile">
        <Navigation {...props} />
        <section className="container">
          <div className="columns section">
            <div className="column">
              <CurrentUserContextProvider>
                <CurrentUserCard setIsOpen={setIsOpen} />
                <UserProfileModal
                  handleModalToggle={handleModalToggle}
                  modalIsOpen={modalIsOpen}
                />
              </CurrentUserContextProvider>
            </div>
            <div className="column is-half">
              <div className="tile is-ancestor is-block">
                <div className="tile is-parent is-vertical is-block">
                  <CurrentUserContextProvider>
                    <ProfileMainTabs
                      activeTab={mainTab}
                      setActiveTab={setMainTab}
                    />

                    {mainTab === ProfileMainTabType.PROJECT && (
                      <ProjectsWrapper />
                    )}

                    {mainTab === ProfileMainTabType.PORTFOLIO && (
                      <PortfolioWrapper />
                    )}
                  </CurrentUserContextProvider>
                </div>
              </div>
            </div>
            <div className="column page-user-profile-sidebar" />
          </div>
        </section>
      </main>
    </Fragment>
  );
};

export default CurrentUserProfile;
